import React, { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, CheckCircle, FileText, LogIn, Mail, Package,
  ShieldCheck, Store, Truck, UploadCloud, UserPlus, X, Zap,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { clientApi, orderApi } from '../../api/client';
import { useCatalogTypes, useCatalogLanguages, useCatalogServices, useDeliveryOptions } from '../../data/subServicesStore';
import { formatTndWithEur } from '../../utils/currency';
import { flagEmoji, langFlagUrl } from '../../utils/langFlags';
import LangFlagSelect from '../common/LangFlagSelect';
import SearchableSelect from '../common/SearchableSelect';
import DashModal from '../dashboard/DashModal';
import { allCountries, civilities } from '../../data/quoteFormOptions';
import { getVilleOptions } from '../../data/geoData';
import '../quote/QuoteRequestForm.css';
import './OrderRequestForm.css';

const ALLOWED_EXTS = ['pdf', 'jpg', 'jpeg', 'png', 'docx', 'doc'];
// Doit correspondre à MAX_UPLOAD_MB côté backend (défaut 500 Mo).
const MAX_FILE_SIZE = 500 * 1024 * 1024;
// Un seul document par commande de traduction ; jusqu'à 5 documents
// préparatoires pour une mission d'interprétariat.
const MAX_FILES = 1;
const MAX_PREP_FILES = 5;

const PRESTATIONS = [
  {
    id: 'translation',
    label: 'Traduction de documents',
    desc: 'Documents personnels, administratifs, officiels ou professionnels — acte de naissance, diplôme, passeport, contrat, relevé de notes, rapport…',
  },
  {
    id: 'interpreting',
    label: 'Interprétariat',
    desc: 'Intervention orale d’un interprète — réunion, conférence, rendez-vous médical, administratif ou mission juridique.',
  },
];

const TRANSLATION_TYPES = [
  {
    id: 'professional',
    label: 'Traduction professionnelle',
    desc: 'Usage général, commercial, technique ou spécialisé — sans certification administrative automatiquement incluse.',
  },
  {
    id: 'sworn',
    label: 'Traduction assermentée',
    desc: 'Effectuée par un traducteur habilité ou agréé selon les règles applicables, avec les éléments officiels prévus.',
  },
  {
    id: 'certified',
    label: 'Traduction certifiée',
    desc: 'Accompagnée d’une certification, d’un cachet ou d’une signature lorsque la prestation et les règles le prévoient.',
  },
];

const DEADLINES = [
  { id: 'standard', label: 'Standard', desc: 'Délai normal' },
  { id: 'fast', label: 'Rapide', desc: 'Traitement prioritaire' },
  { id: 'urgent', label: 'Urgent', desc: 'Traitement accéléré, sous réserve de disponibilité' },
];

// Icônes des options de livraison — la clé vient du champ `icon` du catalogue
const DELIVERY_ICONS = { mail: Mail, store: Store, truck: Truck, zap: Zap };

const DELIVERY_TIMES = ['09:00', '12:00', '15:00', '17:00', '19:00', '21:00'];

const DEADLINE_FLEX = [
  { id: 'strict', label: 'Date et heure impératives' },
  { id: 'flexible', label: 'Date souhaitée, flexible si nécessaire' },
];

// Liste de pays à plat (groupe sans titre) pour SearchableSelect
const COUNTRY_GROUP = {
  label: '',
  items: allCountries.map((c) => ({ value: c.name, label: c.name })),
};

// Suppléments fixes — tarifs par défaut, ajustables par l'administration.
const DEADLINE_EXTRA = { standard: 0, fast: 10, urgent: 20 };
const FORMALITY_EXTRA = { legalization: 20, apostille: 30 };

const FORMALITIES = [
  { id: 'none', label: 'Aucune formalité complémentaire' },
  { id: 'legalization', label: 'Légalisation' },
  { id: 'apostille', label: 'Apostille' },
  { id: 'unsure', label: 'Je ne sais pas, j’ai besoin de conseils' },
];



const STEPS_TRANSLATION = [
  { title: 'Choix de la prestation', hint: 'Traduction de documents ou interprétariat' },
  { title: 'Détails de la prestation', hint: 'Type de traduction, langues et document' },
  { title: 'Documents et formalités', hint: 'Fichiers, destination et options' },
  { title: 'Délais et livraison', hint: 'Priorité, échéance, mode de livraison' },
  { title: 'Coordonnées et récapitulatif', hint: 'Vos informations avant envoi' },
];

const STEPS_INTERPRETING = [
  { title: 'Choix de la prestation', hint: 'Traduction de documents ou interprétariat' },
  { title: 'Mission', hint: 'Type, langues, mode et horaires' },
  { title: 'Contexte et documents', hint: 'Sujet et documents préparatoires' },
  { title: 'Délai', hint: 'Priorité et informations complémentaires' },
  { title: 'Coordonnées et récapitulatif', hint: 'Vos informations avant envoi' },
];

const fileExt = (name) => String(name || '').split('.').pop().toLowerCase();
const formatSize = (bytes) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} Ko`
    : `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;

/** Trouve le service catalogue dont le tarif s'applique (slug, type ou tag). */
function findServiceFor(services, slug) {
  const wanted = String(slug || '').toLowerCase();
  if (!wanted) return null;
  return services.find((s) => {
    const tags = (s.tags || []).map((t) => String(t).toLowerCase());
    return (
      String(s.slug || '').toLowerCase() === wanted
      || String(s.type || '').toLowerCase() === wanted
      || tags.includes(wanted)
    );
  }) || null;
}

const unitPrice = (svc, unit) => {
  if (!svc) return 0;
  const p = svc.prices || {};
  const n = Number(p[unit]);
  if (Number.isFinite(n) && n > 0) return n;
  // Même repli que pricing.js : basePrice ne vaut que pour « document ».
  return unit === 'document' ? Number(svc.basePrice) || 0 : 0;
};

/**
 * Tarification depuis le catalogue : prix par document × fichiers (traduction)
 * ou prix horaire × durée (interprétariat). pending=true si aucun tarif
 * applicable → l'admin valide le prix avant paiement.
 */
/**
 * Total = Traduction + Supplément type + Supplément délai + Formalités + Livraison.
 * - document : tarif × fichiers ; page : tarif × volume estimé.
 * - pending = aucun tarif catalogue applicable → l'admin valide le prix.
 * - toConfirm = frais de formalité / livraison postale à confirmer.
 */
// Tarif de livraison applicable : countryFees[pays] prioritaire sur le tarif
// par défaut ; null = « sur devis / à confirmer ».
function resolveDeliveryFee(option, country) {
  if (!option) return null;
  const byCountry = option.countryFees && country ? option.countryFees[country] : undefined;
  const fee = byCountry != null ? Number(byCountry) : option.fee;
  return fee == null ? null : Number(fee);
}

function computePrice(form, services, files, deliveryOptions) {
  const breakdown = [];
  let total = 0;
  let pending = false;
  let toConfirm = false;

  if (form.prestation === 'translation') {
    const items = files.length ? files : [{ docType: form.docType }];
    // Méthode de tarification configurée dans le catalogue (svc.unit) en priorité,
    // puis repli sur les autres tarifs déclarés du service. Le volume (pages,
    // mots) vient du comptage automatique des fichiers — pas de saisie client.
    for (const f of items) {
      const svc = findServiceFor(services, f.docType);
      // Tarif par type de traduction : typePricing[type] prime sur unit/prices.
      const tp = (svc?.typePricing || []).find((e) => e.type === form.translationType);
      const tpRate = tp && Number(tp.price) > 0 ? Number(tp.price) : 0;
      const hasPrice = (u) => Number(svc?.prices?.[u]) > 0
        || (u === 'document' && unitPrice(svc, 'document') > 0);
      const unitOrder = tpRate
        ? [tp.method]
        : [svc?.unit, 'document', 'page', 'word']
            .filter((u, i, arr) => arr.indexOf(u) === i && hasPrice(u));
      const detectedPages = f.analysis?.pages || f.analysis?.billablePages || 0;
      const detectedWords = f.analysis?.words || 0;
      let priced = false;
      let limitExceeded = false;
      for (const unit of unitOrder) {
        const rate = tpRate || Number(svc?.prices?.[unit])
          || (unit === 'document' ? unitPrice(svc, 'document') : 0);
        if (!rate) continue;
        let qty;
        if (unit === 'word') {
          qty = detectedWords;
        } else if (unit === 'page') {
          qty = detectedPages;
        } else {
          // Prix fixe par document : réservé aux documents standardisés.
          if (svc.priceLimit > 0 && detectedPages > svc.priceLimit) {
            limitExceeded = true;
            continue;
          }
          qty = 1;
        }
        // Volume pas encore analysé : le minimum facturable sert d'estimation
        // de départ (le volume réel vient de l'analyse des fichiers).
        qty = Math.max(qty, Number(svc.minQty) || 0);
        if (!qty) {
          // Tarif connu mais quantité indéterminée → afficher le taux appliqué.
          const uLbl = { word: 'par mot', page: 'par page', document: 'par document' }[unit] || '';
          breakdown.push({
            label: `${svc.titleFr || f.docType} · ${rate} TND ${uLbl}`,
            note: 'Quantité calculée à l\'analyse des fichiers',
            value: 0,
            // Même affichage que les lignes tarifées : le tarif unitaire.
            displayValue: formatTndWithEur(rate, 'fr-FR', 'TND'),
          });
          priced = true; // ligne informative affichée ; le total reste à confirmer
          pending = true;
          break;
        }
        // Minimum de facturation : typePricing d'abord, sinon le service.
        const min = tpRate ? Number(tp.minPrice) || 0 : Number(svc.minPrice) || 0;
        const amount = Math.max(rate * qty, min);
        const unitLbl = { word: 'par mot', page: 'par page', document: 'par document' }[unit] || '';
        const qtyLabel = unit === 'word' || unit === 'page' ? ` × ~${qty}` : '';
        breakdown.push({ label: `${svc.titleFr || f.docType} · ${rate} TND ${unitLbl}${qtyLabel}`, value: amount });
        total += amount;
        priced = true;
        break;
      }
      if (!priced) {
        pending = true;
        breakdown.push({
          label: limitExceeded
            ? 'Document au-delà de la limite standard — prix à vérifier'
            : `${svc?.titleFr || f.docType || 'Document'} — prix à confirmer`,
          value: 0,
        });
      }
    }
    // Pas de supplément « type » : le tarif par type est déjà dans typePricing.
  } else {
    const svc = findServiceFor(services, form.interpretingType);
    // Mission multi-jours : durée par jour × nombre de jours (inclus).
    const days = form.multiDay && form.missionDate && form.missionEndDate
      ? Math.max(1, Math.round((new Date(form.missionEndDate) - new Date(form.missionDate)) / 86400000) + 1)
      : 1;
    const hours = Math.max(1, Number(form.duration) || 0) * days;
    // Même logique que pricing.js : unité configurée (heure, demi-journée
    // ~4 h, journée ~8 h, mission), sinon première unité tarifée.
    const interpUnits = ['hour', 'halfday', 'day', 'service'];
    const unit = [svc?.unit, ...interpUnits].find(
      (u, i, arr) => u && arr.indexOf(u) === i && unitPrice(svc, u) > 0
    );
    const rate = unitPrice(svc, unit);
    const qty = unit === 'service' ? 1
      : unit === 'day' ? Math.ceil(hours / 8)
      : unit === 'halfday' ? Math.ceil(hours / 4)
      : hours;
    if (!rate || !form.duration) {
      pending = true;
      breakdown.push({
        label: `${svc?.titleFr || 'Interprétariat'} — prix à confirmer`,
        value: 0,
      });
    } else {
      const amount = Math.max(rate * qty, Number(svc?.minPrice) || 0);
      const lbl = { hour: 'par heure', halfday: 'par demi-journée', day: 'par journée', service: 'par mission' }[unit] || 'par heure';
      const qtyTxt = unit === 'service' ? '' : ` × ~${qty}`;
      breakdown.push({ label: `${svc?.titleFr || 'Interprétariat'} · ${rate} TND ${lbl}${qtyTxt}`, value: amount });
      total += amount;
    }
  }

  const deadlineExtra = DEADLINE_EXTRA[form.deadline] || 0;
  if (deadlineExtra && !pending) {
    breakdown.push({ label: 'Délai rapide / urgent', value: deadlineExtra });
    total += deadlineExtra;
  }

  if (form.prestation === 'translation' && form.formality && form.formality !== 'none') {
    const fee = FORMALITY_EXTRA[form.formality] || 0;
    if (fee > 0) {
      const lbl = FORMALITIES.find((f) => f.id === form.formality)?.label || 'Complémentaire';
      breakdown.push({ label: `Formalité — ${lbl}`, value: fee });
      total += fee;
    } else {
      breakdown.push({ label: 'Formalité — faisabilité et prix à confirmer', value: 0 });
      toConfirm = true;
    }
  }

  const deliveryOpt = (deliveryOptions || []).find((o) => o.code === form.deliveryMode);
  if (deliveryOpt) {
    const fee = resolveDeliveryFee(deliveryOpt, form.deliveryCountry);
    if (fee == null) {
      breakdown.push({ label: `${deliveryOpt.labelFr} — frais selon la destination`, value: 0 });
      toConfirm = true;
    } else if (fee > 0) {
      breakdown.push({ label: deliveryOpt.labelFr, value: fee });
      total += fee;
    }
  }

  return { breakdown, total, pending, toConfirm };
}

function Field({ label, required, full, children }) {
  return (
    <div className={`qf-field${full ? ' qf-field--full' : ''}`}>
      <label className="qf-label">
        {label}{required && <span aria-hidden="true"> *</span>}
      </label>
      {children}
    </div>
  );
}

export default function OrderRequestForm() {
  const { user, isAuthenticated } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const prepInputRef = useRef(null);

  const [guestMode, setGuestMode] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    prestation: 'translation',
    translationType: 'professional',
    sourceLang: '',
    targetLang: '',
    docType: '',
    docTypeOther: '',
    deadline: 'standard',
    useCountry: '',
    recipientOrg: '',
    deliveryDate: '',
    deliveryTime: '17:00',
    deadlineFlex: 'strict',
    formality: 'none',
    termsAccepted: false,
    interpretingType: '',
    interpretingMode: '',
    missionDate: '',
    missionEndDate: '',
    multiDay: false,
    missionStartTime: '',
    duration: '',
    missionCity: '',
    missionAddress: '',
    meetingLink: '',
    participants: '',
    subject: '',
    civility: user?.civility || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    deliveryMode: 'digital',
    recipient: '',
    address: '',
    postalCode: '',
    deliveryCity: '',
    deliveryCountry: 'Tunisie',
    instructions: '',
  });
  const [files, setFiles] = useState([]); // [{ file, docType, docTypeOther }] — traduction (1 seul)
  const [interpFiles, setInterpFiles] = useState([]); // documents préparatoires interprétariat
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const catalogTypes = useCatalogTypes();
  const catalogLangs = useCatalogLanguages();
  const catalogServices = useCatalogServices();
  const deliveryOpts = useDeliveryOptions();
  // Option de livraison choisie, depuis le catalogue admin (deliveryOptions).
  const selectedDelivery = useMemo(
    () => deliveryOpts.options.find((o) => o.code === form.deliveryMode) || null,
    [deliveryOpts.options, form.deliveryMode]
  );

  // Liste des villes selon le pays de livraison (Tunisie → gouvernorats).
  const deliveryCountryCode = useMemo(
    () => allCountries.find((c) => c.name === form.deliveryCountry)?.code || '',
    [form.deliveryCountry]
  );
  const deliveryVilleOptions = useMemo(
    () => getVilleOptions(deliveryCountryCode),
    [deliveryCountryCode]
  );

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const langName = (l) =>
    (lang === 'ar' && l.nameAr) || (lang === 'en' && l.nameEn) || l.nameFr || l.nativeName || l.code;

  // Métadonnées d'une langue (drapeau image + nom) — les valeurs sont les codes
  const langMeta = (value) => {
    const found = catalogLangs.languages.find((l) => l.code === value);
    return {
      name: found ? langName(found) : value,
      flagUrl: langFlagUrl(found),
      emoji: flagEmoji(found?.flag),
    };
  };
  const typeName = (tp) =>
    (lang === 'ar' && tp.titleAr) || (lang === 'en' && tp.titleEn) || tp.titleFr || tp.slug;

  const svcName = (s) =>
    (lang === 'ar' && s.titleAr) || (lang === 'en' && s.titleEn) || s.titleFr || s.slug;

  // « Type de document » = services du catalogue, groupés par catégorie de
  // document — la catégorie est cherchée dans `type` puis dans `tags`
  // (l'admin la coche dans le formulaire du sous-service).
  const docGroups = useMemo(() => {
    const svcs = catalogServices.services.filter(
      (s) => s.category === 'translation' && s.isActive !== false
        // « Services autorisés » : le document doit autoriser le type choisi —
        // sans typePricing = non restreint, proposé pour tous les types.
        && (!(s.typePricing || []).length
          || s.typePricing.some((e) => e.type === form.translationType))
    );
    const categories = catalogTypes.types.filter((tp) => tp.group === 'documents');
    const catOf = (s) => {
      const cand = [s.type, ...(s.tags || [])].map((v) => String(v || '').toLowerCase());
      const found = categories.find((c) => cand.includes(String(c.slug).toLowerCase()));
      return found ? found.slug : s.type || 'other';
    };
    const byType = new Map();
    for (const s of svcs) {
      const key = catOf(s);
      if (!byType.has(key)) byType.set(key, []);
      byType.get(key).push(s);
    }
    return [...byType.entries()].map(([type, items]) => ({
      type,
      label: typeName(categories.find((c) => c.slug === type) || {}) || type,
      items,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogServices.services, catalogTypes.types, lang, form.translationType]);

  // Groupes au format SearchableSelect
  const docSelectGroups = useMemo(
    () => docGroups.map((g) => ({
      label: g.label,
      items: g.items.map((s) => ({ value: s.slug, label: svcName(s) })),
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [docGroups, lang]
  );
  const interpretingTypes = useMemo(
    () => catalogTypes.types.filter((tp) => tp.group === 'interpreting'),
    [catalogTypes.types]
  );
  const interpretingModes = useMemo(
    () => catalogTypes.types.filter((tp) => tp.group === 'modes'),
    [catalogTypes.types]
  );

  // Sous-services d'interprétariat du catalogue (base de données), groupés par
  // type de mission — alimente « Type de mission ». Le parent est cherché dans
  // `type` puis dans `tags` (l'admin le choisit comme « Service principal »).
  const interpGroups = useMemo(() => {
    const svcs = catalogServices.services.filter(
      (s) => (s.category === 'interpreting' || s.type === 'interpreting') && s.isActive !== false
    );
    const byParent = new Map();
    for (const s of svcs) {
      const cand = [s.type, ...(s.tags || [])].map((v) => String(v || '').toLowerCase());
      const parent = interpretingTypes.find((tp) => cand.includes(String(tp.slug).toLowerCase()));
      const key = parent ? parent.slug : 'other';
      if (!byParent.has(key)) {
        byParent.set(key, { label: parent ? typeName(parent) : 'Autre', items: [] });
      }
      byParent.get(key).items.push({ value: s.slug, label: svcName(s) });
    }
    return [...byParent.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogServices.services, interpretingTypes, lang]);

  // Libellé du type de mission : sous-service du catalogue, sinon type statique.
  const interpTypeLabel = (slug) => {
    const svc = catalogServices.services.find(
      (s) => String(s.slug).toLowerCase() === String(slug || '').toLowerCase()
    );
    return svc ? svcName(svc) : typeName(interpretingTypes.find((tp) => tp.slug === slug) || {});
  };

  const price = useMemo(
    () => computePrice(form, catalogServices.services, files, deliveryOpts.options),
    [form, catalogServices.services, files, deliveryOpts.options]
  );

  // ---- fichiers ----
  const addFiles = (list) => {
    const next = [];
    let rejected = '';
    for (const file of Array.from(list || [])) {
      if (!ALLOWED_EXTS.includes(fileExt(file.name))) {
        rejected = `« ${file.name} » — format non accepté (PDF, JPG, PNG, DOCX uniquement).`;
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        rejected = `« ${file.name} » dépasse 500 Mo.`;
        continue;
      }
      const isImage = ['jpg', 'jpeg', 'png'].includes(fileExt(file.name));
      next.push({
        file,
        docType: form.docType,
        docTypeOther: '',
        analysis: null,
        analyzing: true,
        preview: isImage ? URL.createObjectURL(file) : '',
      });
    }
    // Un seul document : un nouveau dépôt remplace le précédent.
    setFiles(next.slice(0, MAX_FILES));
    setErrors((e) => ({ ...e, files: rejected || undefined }));

    // Comptage automatique des mots/pages pour la tarification configurée
    // dans le catalogue (document, page ou mot selon le type de document).
    if (next.length) {
      orderApi.analyzeFiles(next.map((n) => n.file))
        .then((res) => {
          const analyses = res?.files || [];
          setFiles((prev) => prev.map((entry) => {
            const idx = next.findIndex((n) => n.file === entry.file);
            return idx >= 0
              ? { ...entry, analysis: analyses[idx] || null, analyzing: false }
              : entry;
          }));
        })
        .catch(() => {
          setFiles((prev) => prev.map((entry) => (
            next.some((n) => n.file === entry.file) ? { ...entry, analyzing: false } : entry
          )));
        });
    }
  };

  const removeFile = (i) => setFiles((prev) => {
    const removed = prev[i];
    if (removed?.preview) URL.revokeObjectURL(removed.preview);
    return prev.filter((_, idx) => idx !== i);
  });
  const setFileType = (i, patch) =>
    setFiles((prev) => prev.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));

  // Documents préparatoires d'une mission d'interprétariat (multi-fichiers).
  // Ils ne participent pas à la tarification, mais le nombre de mots/pages est
  // compté par fichier pour aider l'interprète à préparer la mission.
  const addPrepFiles = (list) => {
    const next = [];
    for (const file of Array.from(list || [])) {
      if (!ALLOWED_EXTS.includes(fileExt(file.name))) continue;
      if (file.size > MAX_FILE_SIZE) continue;
      const isImage = ['jpg', 'jpeg', 'png'].includes(fileExt(file.name));
      next.push({ file, preview: isImage ? URL.createObjectURL(file) : '', analysis: null, analyzing: true });
    }
    if (!next.length) return;
    setInterpFiles((prev) => [...prev, ...next].slice(0, MAX_PREP_FILES));
    orderApi.analyzeFiles(next.map((n) => n.file))
      .then((res) => {
        const analyses = res?.files || [];
        setInterpFiles((prev) => prev.map((entry) => {
          const idx = next.findIndex((n) => n.file === entry.file);
          return idx >= 0 ? { ...entry, analysis: analyses[idx] || null, analyzing: false } : entry;
        }));
      })
      .catch(() => {
        setInterpFiles((prev) => prev.map((entry) => (
          next.some((n) => n.file === entry.file) ? { ...entry, analyzing: false } : entry
        )));
      });
  };
  const removePrepFile = (i) => setInterpFiles((prev) => {
    const removed = prev[i];
    if (removed?.preview) URL.revokeObjectURL(removed.preview);
    return prev.filter((_, idx) => idx !== i);
  });

  // Pays/organisme/document original : requis si formalité demandée,
  // traduction assermentée/certifiée, ou exigé par le document (catalogue).
  const docSvc = findServiceFor(catalogServices.services, form.docType);
  // Types de traduction autorisés pour le document choisi (admin : « Services
  // autorisés » → présents dans typePricing, même sans prix = sur devis).
  // Repli sur les trois types si le document ne restreint rien.
  const allowedTTypes = useMemo(() => {
    const ids = (docSvc?.typePricing || []).map((e) => e.type);
    return ids.length ? TRANSLATION_TYPES.filter((tp) => ids.includes(tp.id)) : TRANSLATION_TYPES;
  }, [docSvc]);
  // Une formalité (légalisation / apostille / « je ne sais pas ») demande des
  // informations complémentaires ; « Aucune » n'en demande aucune.
  const formalityRequested = ['legalization', 'apostille', 'unsure'].includes(form.formality);
  // Pays / organisme / document original : uniquement si une formalité est demandée.
  const needsDestination = form.prestation === 'translation' && formalityRequested;

  // Mode d'intervention « présentiel » (slug catalogue contenant le mot-clé).
  const isOnsite = /presentiel|onsite|on-site/i.test(form.interpretingMode);
  const isRemote = /visio|video|remote/i.test(form.interpretingMode);

  // ---- validation par étape ----
  const validateStep = (s) => {
    const e = {};
    if (s === 1 && form.prestation === 'translation' && !form.translationType) {
      e.translationType = 'Sélectionnez le type de traduction.';
    }
    if (s === 2) {
      if (!form.sourceLang) e.sourceLang = 'Sélectionnez la langue source.';
      if (!form.targetLang) e.targetLang = 'Sélectionnez la langue cible.';
      if (form.sourceLang && form.sourceLang === form.targetLang) e.targetLang = 'Les deux langues doivent être différentes.';
      if (form.prestation === 'translation') {
        if (!form.docType) e.docType = 'Sélectionnez le type de document.';
        if (form.docType === 'other' && !form.docTypeOther.trim()) e.docTypeOther = 'Précisez le type de document.';
      } else {
        if (!form.interpretingType) e.interpretingType = 'Sélectionnez le type de mission.';
        if (!form.interpretingMode) e.interpretingMode = 'Sélectionnez le mode d’intervention.';
        if (!form.missionDate) e.missionDate = 'Indiquez la date de la mission.';
        if (!(Number(form.duration) > 0)) e.duration = 'Indiquez la durée estimée (heures).';
        if (form.multiDay && !form.missionEndDate) e.missionEndDate = 'Date de fin requise.';
        else if (form.multiDay && form.missionDate && form.missionEndDate < form.missionDate) e.missionEndDate = 'La date de fin doit être après le début.';
        if (isOnsite) {
          if (!form.missionCity.trim()) e.missionCity = 'Ville de la mission requise.';
          if (!form.missionAddress.trim()) e.missionAddress = 'Adresse de la mission requise.';
        }
      }
    }
    if (s === 3 && form.prestation === 'translation') {
      if (!files.length) e.files = 'Ajoutez au moins un document à traduire.';
      for (const f of files) {
        if (files.length > 1 && !f.docType) e.files = 'Associez un type de document à chaque fichier.';
        if (f.docType === 'other' && !f.docTypeOther.trim()) e.files = 'Précisez le type pour chaque fichier « Autre ».';
      }
      if (!form.formality) e.formality = 'Sélectionnez une option.';
      if (needsDestination && !form.useCountry) e.useCountry = 'Indiquez le pays où le document sera utilisé.';

    }
    if (s === 4 && form.prestation === 'translation') {
      if (!form.deliveryDate) e.deliveryDate = 'Choisissez la date de réception souhaitée.';
      else if (new Date(`${form.deliveryDate}T23:59`) < new Date()) e.deliveryDate = 'La date doit être dans le futur.';
      if (!form.deadlineFlex) e.deadlineFlex = 'Précisez la flexibilité du délai.';
      if (selectedDelivery?.requiresAddress) {
        if (!form.recipient.trim()) e.recipient = 'Nom du destinataire requis.';
        if (!form.address.trim()) e.address = 'Adresse de livraison requise.';
        if (!form.deliveryCity.trim()) e.deliveryCity = 'Ville requise.';
        if (!form.deliveryCountry.trim()) e.deliveryCountry = 'Pays de livraison requis.';
      }
    }
    if (s === 5) {
      if (!form.civility) e.civility = 'Civilité requise.';
      if (!form.firstName.trim()) e.firstName = 'Prénom requis.';
      if (!form.lastName.trim()) e.lastName = 'Nom requis.';
      if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Adresse email invalide.';
      if (!form.phone.trim()) e.phone = 'Téléphone requis.';
      if (!form.termsAccepted) e.terms = 'Vous devez accepter les conditions de service.';
    }
    setErrors(e);
    return !Object.keys(e).length;
  };

  const goNext = () => { if (validateStep(step)) setStep(step + 1); };
  const goPrev = () => setStep(Math.max(1, step - 1));

  // ---- envoi ----
  // Clic « Envoyer ma demande » : valide tout, puis demande confirmation avant
  // d'envoyer les coordonnées au serveur.
  const handleSubmit = () => {
    for (const s of [1, 2, 3, 4, 5]) {
      if (!validateStep(s)) {
        setStep(s);
        return;
      }
    }
    setConfirmOpen(true);
  };

  const doSubmit = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    setSubmitError('');

    const allDocServices = docGroups.flatMap((g) => g.items);
    const docTypeLabel = form.docType === 'other'
      ? form.docTypeOther.trim()
      : svcName(allDocServices.find((s) => s.slug === form.docType) || {});

    const payload = {
      document: form.prestation === 'translation'
        ? (docTypeLabel || 'Traduction de documents')
        : (interpTypeLabel(form.interpretingType) || 'Interprétariat'),
      category: form.prestation,
      sourceLang: form.sourceLang,
      targetLang: form.targetLang,
      langPairs: [{ source: form.sourceLang, target: form.targetLang }],
      price: price.pending ? 0 : price.total,
      currency: 'TND',
      deadline: form.deadline,
      deadlineDate: form.prestation === 'interpreting' ? form.missionDate : form.deliveryDate,
      delivery: form.deliveryMode,
      deliveryFee: resolveDeliveryFee(selectedDelivery, form.deliveryCountry) || 0,
      deliveryAddress: selectedDelivery?.requiresAddress
        ? [form.address, form.postalCode, form.deliveryCity, form.deliveryCountry].filter(Boolean).join(', ')
        : '',
      deliveryRecipient: selectedDelivery?.requiresAddress
        ? {
            name: form.recipient.trim() || `${form.firstName} ${form.lastName}`.trim(),
            phone: form.phone,
            country: form.deliveryCountry,
            city: form.deliveryCity,
            address: form.address,
            postalCode: form.postalCode,
          }
        : null,
      comments: form.instructions,
      civility: form.civility,
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phone: form.phone,
      details: {
        translationType: form.translationType,
        useCountry: form.useCountry,
        recipientOrg: form.recipientOrg,
        formality: form.prestation === 'translation' ? form.formality : 'none',
        deliveryDate: form.deliveryDate,
        deliveryTime: form.deliveryTime,
        deadlineFlex: form.deadlineFlex,
        docType: form.docType,
        docTypeOther: form.docTypeOther,
        fileDocTypes: form.prestation === 'translation' ? files.map((f) => ({
          name: f.file.name,
          docType: f.docType || form.docType,
          docTypeOther: f.docTypeOther || '',
          words: f.analysis?.words || 0,
          billablePages: f.analysis?.billablePages || 0,
          wordsNeedReview: Boolean(f.analysis?.needsReview),
        })) : [],
        detectedWords: files.reduce((s, f) => s + (f.analysis?.words || 0), 0),
        detectedPages: files.reduce((s, f) => s + (f.analysis?.pages || f.analysis?.billablePages || 0), 0),
        detectedBillablePages: files.reduce((s, f) => s + (f.analysis?.billablePages || 0), 0),
        // Nombre de mots/pages par document préparatoire (interprétariat).
        prepFiles: form.prestation === 'interpreting' ? interpFiles.map((f) => ({
          name: f.file.name,
          words: f.analysis?.words || 0,
          pages: f.analysis?.pages || f.analysis?.billablePages || 0,
        })) : [],
        interpretingType: form.interpretingType,
        interpretingMode: form.interpretingMode,
        missionDate: form.missionDate,
        missionEndDate: form.multiDay ? form.missionEndDate : '',
        missionStartTime: form.missionStartTime,
        duration: form.duration,
        missionCity: form.missionCity,
        missionAddress: form.missionAddress,
        meetingLink: form.meetingLink,
        participants: form.participants,
        subject: form.subject,
        deliveryMode: form.deliveryMode,
        postalCode: form.postalCode,
        pricePending: price.pending,
        pricingBreakdown: price.breakdown,
        estimatedPrice: price.total,
      },
      // Traduction : le document à traduire ; interprétariat : les documents
      // préparatoires (facultatifs).
      fileNames: (form.prestation === 'translation' ? files : interpFiles).map((f) => f.file.name),
    };

    const filesToSend = form.prestation === 'translation' ? files : interpFiles;
    try {
      const res = isAuthenticated
        ? await clientApi.createOrder(payload, filesToSend.map((f) => f.file))
        : await orderApi.create(payload, filesToSend.map((f) => f.file));
      setSubmitted({
        order: res.order,
        trackingToken: res.trackingToken || '',
        trackingUrl: res.trackingUrl || '',
        guest: Boolean(res.guest),
      });
    } catch (err) {
      const code = err.data?.error;
      setSubmitError(
        code === 'emailHasAccount'
          ? 'Cette adresse e-mail est liée à un compte existant. Connectez-vous pour commander.'
          : code === 'fileTypeNotAllowed'
            ? 'Un fichier a été refusé — formats acceptés : PDF, JPG, PNG, DOCX.'
            : code === 'fileTooLarge'
              ? 'Un fichier dépasse la taille maximale de 500 Mo.'
              : code === 'invalidClient' || code === 'invalidPayload'
                ? 'Vérifiez les informations saisies puis réessayez.'
                : 'Erreur lors de l’envoi de la commande. Veuillez réessayer.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ---- écran de succès ----
  if (submitted) {
    const code = submitted.order?.id || '';
    const trackUrl = `/suivi?code=${encodeURIComponent(code)}&token=${encodeURIComponent(submitted.trackingToken)}`;
    return (
      <div className="qf-success">
        <div className="qf-success-icon"><CheckCircle size={40} /></div>
        <h2>Commande {code} créée</h2>
        <p>
          Votre demande est enregistrée. Nous recherchons un professionnel compatible —
          {price.pending
            ? ' le prix sera validé par notre équipe, puis vous pourrez régler votre commande.'
            : ` vous pourrez régler ${formatTndWithEur(price.total)} dès qu’un professionnel aura accepté votre commande.`}
        </p>
        {submitted.guest && (
          <p className="qf-track-hint">
            <ShieldCheck size={14} /> Un lien de suivi sécurisé a été envoyé à votre adresse email.
          </p>
        )}
        <div className="qf-success-actions">
          {submitted.guest ? (
            <Link to={trackUrl} className="btn btn-primary">Suivre ma commande</Link>
          ) : (
            <Link to="/app/orders" className="btn btn-primary">Voir mes commandes</Link>
          )}
          <Link to="/" className="btn btn-outline">Retour à l’accueil</Link>
        </div>
      </div>
    );
  }

  // ---- écran d'accueil : compte ou invité ----
  if (!isAuthenticated && !guestMode) {
    return (
      <div className="qf-gate card">
        <h2>Comment souhaitez-vous commander ?</h2>
        <p>Connectez-vous, créez un compte, ou continuez sans compte — le suivi se fera par e-mail.</p>
        <div className="qf-gate-actions">
          <button type="button" className="qf-gate-option" onClick={() => navigate('/login', { state: { from: '/order' } })}>
            <LogIn size={22} />
            <strong>Se connecter</strong>
            <small>J&apos;ai déjà un compte Tarjemlek</small>
            <small className="qf-gate-desc">Suivez et gérez vos commandes depuis votre espace client.</small>
          </button>
          <button type="button" className="qf-gate-option" onClick={() => navigate('/register', { state: { from: '/order' } })}>
            <UserPlus size={22} />
            <strong>Créer un compte</strong>
            <small>Je souhaite créer un compte</small>
            <small className="qf-gate-desc">Accédez à votre espace client et retrouvez toutes vos commandes.</small>
          </button>
          <button type="button" className="qf-gate-option" onClick={() => setGuestMode(true)}>
            <Package size={22} />
            <strong>Continuer sans compte</strong>
            <small>Commande rapide</small>
            <small className="qf-gate-desc">Aucun mot de passe requis. Le suivi se fera par e-mail.</small>
          </button>
        </div>
        <p className="qf-gate-note">Aucun mot de passe n&apos;est demandé pour une commande sans compte.</p>
      </div>
    );
  }

  const langs = catalogLangs.languages;
  const catalogOffline = catalogLangs.offline || catalogTypes.offline || catalogServices.offline;
  const steps = form.prestation === 'translation' ? STEPS_TRANSLATION : STEPS_INTERPRETING;
  const progress = (step / steps.length) * 100;
  const stepDef = steps[step - 1];

  return (
    <div className="qf-shell">
      <aside className="qf-side">
        <nav className="qf-side-card" aria-label="Étapes">
          <p className="qf-side-title">Votre commande</p>
          <ol className="qf-steps">
            {steps.map((s, i) => {
              const n = i + 1;
              return (
                <li key={n} className={`qf-steps-item${step > n ? ' done' : ''}${step === n ? ' current' : ''}`}>
                  <button type="button" disabled aria-current={step === n ? 'step' : undefined}>
                    <span className="qf-steps-num">{step > n ? <Check size={14} /> : n}</span>
                    <span className="qf-steps-text">
                      <strong>{s.title}</strong>
                      <small>{s.hint}</small>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        <div className="qf-price-card">
          <p className="qf-price-title">Estimation du prix</p>
          {price.pending ? (
            price.breakdown.length ? (
              <>
                <dl className="qf-price-list">
                  {price.breakdown.map((item, i) => (
                    <div key={i}>
                      <dt>
                        {item.label}
                        {item.note && <em className="qf-price-note">{item.note}</em>}
                      </dt>
                      <dd>{item.displayValue || (item.value > 0 ? formatTndWithEur(item.value, 'fr-FR', 'TND') : '—')}</dd>
                    </div>
                  ))}
                </dl>
                <p className="qf-price-payment" style={{ marginTop: 10 }}>
                  Le total sera calculé après analyse de vos fichiers — notre équipe confirmera le prix avant le paiement.
                </p>
              </>
            ) : (
              <p className="qf-price-empty">
                Aucun tarif applicable pour cette sélection — notre équipe confirmera le prix avant le paiement.
              </p>
            )
          ) : price.total > 0 ? (
            <>
              <dl className="qf-price-list">
                {price.breakdown.map((item, i) => (
                  <div key={i}>
                    <dt>
                      {item.label}
                      {item.note && <em className="qf-price-note">{item.note}</em>}
                    </dt>
                    <dd>{formatTndWithEur(item.value, 'fr-FR', 'TND')}</dd>
                  </div>
                ))}
              </dl>
              <div className="qf-price-total">
                <span>Total estimé</span>
                <strong>{formatTndWithEur(price.total, 'fr-FR', 'TND')}</strong>
              </div>
              <p className="qf-price-payment" style={{ marginTop: 10 }}>
                {price.toConfirm
                  ? 'Montant indicatif — les frais de formalité et/ou de livraison seront confirmés par l’équipe.'
                  : "Le prix définitif est validé par l'admin après analyse."}
              </p>
            </>
          ) : (
            <p className="qf-price-empty">Sélectionnez un type de document ou de mission pour afficher le prix.</p>
          )}
        </div>

        <div className="qf-help">
          <p className="qf-help-title">Besoin d&apos;aide ?</p>
          <p className="qf-help-text">Notre équipe vous accompagne pour préparer votre demande.</p>
        </div>
      </aside>

      <form className="qf" onSubmit={(e) => e.preventDefault()} noValidate>
        <header className="qf-head">
          <span className="qf-eyebrow">Étape {step} / {steps.length}</span>
          <h2>{stepDef.title}</h2>
          <p>{stepDef.hint}</p>
          <div className="qf-bar" aria-hidden="true">
            <span style={{ width: `${progress}%` }} />
          </div>
        </header>

        <div className="qf-body">
          {submitError && <p className="qf-error" role="alert">{submitError}</p>}
          {catalogOffline && (
            <p className="qf-error" role="alert">
              Le catalogue est momentanément indisponible — vérifiez votre connexion puis rechargez la page.
            </p>
          )}

          {step === 1 && (
            <div className="qf-stack">
              <Field label="Que souhaitez-vous commander ?" required>
                <div className="of-presta">
                  {PRESTATIONS.map((p) => (
                    <label key={p.id} className={`of-presta-card${form.prestation === p.id ? ' active' : ''}`}>
                      <input
                        type="radio"
                        name="prestation"
                        checked={form.prestation === p.id}
                        onChange={() => { set({ prestation: p.id }); setFiles([]); setInterpFiles([]); }}
                      />
                      <strong>{p.label}</strong>
                      <small>{p.desc}</small>
                    </label>
                  ))}
                </div>
                <small className="qf-hint">
                  Le choix détermine les champs affichés aux étapes suivantes — vous ne remplissez que les informations relatives à votre service.
                </small>
              </Field>
              {form.prestation === 'translation' && (
                <Field label="Type de traduction" required>
                  <div className="of-presta of-presta--3">
                    {allowedTTypes.map((tp) => (
                      <label key={tp.id} className={`of-presta-card${form.translationType === tp.id ? ' active' : ''}`}>
                        <input
                          type="radio"
                          name="ttype"
                          checked={form.translationType === tp.id}
                          onChange={() => {
                            // Si le document choisi n'autorise pas ce type → réinitialiser
                            const svc = findServiceFor(catalogServices.services, form.docType);
                            const allowed = (svc?.typePricing || []).map((e) => e.type);
                            const docOk = !svc || !allowed.length || allowed.includes(tp.id);
                            set({
                              translationType: tp.id,
                              // Pays/organisme : uniquement assermentée et certifiée
                              ...(tp.id === 'professional' ? { useCountry: '', recipientOrg: '' } : {}),
                              ...(docOk ? {} : { docType: '' }),
                            });
                            if (!docOk) setFiles((prev) => prev.map((f) => ({ ...f, docType: '' })));
                          }}
                        />
                        <strong>{tp.label}</strong>
                        <small>{tp.desc}</small>
                      </label>
                    ))}
                  </div>
                  {errors.translationType && <small className="qf-hint qf-error-text">{errors.translationType}</small>}
                </Field>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="qf-stack">
              {form.prestation !== 'translation' && (
                <div className="qf-grid">
                  <Field label="Type de mission" required>
                    {interpGroups.length ? (
                      <SearchableSelect
                        value={form.interpretingType}
                        onChange={(v) => set({ interpretingType: v })}
                        groups={interpGroups}
                        placeholder="— Sélectionner —"
                        ariaLabel="Type de mission"
                      />
                    ) : (
                      <select value={form.interpretingType} onChange={(e) => set({ interpretingType: e.target.value })}>
                        <option value="">— Sélectionner —</option>
                        {interpretingTypes.map((tp) => <option key={tp.slug} value={tp.slug}>{typeName(tp)}</option>)}
                      </select>
                    )}
                    {errors.interpretingType && <small className="qf-hint qf-error-text">{errors.interpretingType}</small>}
                  </Field>
                  <Field label="Mode d’intervention" required>
                    <select value={form.interpretingMode} onChange={(e) => set({ interpretingMode: e.target.value })}>
                      <option value="">— Sélectionner —</option>
                      {interpretingModes.map((tp) => <option key={tp.slug} value={tp.slug}>{typeName(tp)}</option>)}
                    </select>
                    {errors.interpretingMode && <small className="qf-hint qf-error-text">{errors.interpretingMode}</small>}
                  </Field>
                </div>
              )}

              {form.prestation === 'translation' && (
                <Field label="Type de document" required>
                  <SearchableSelect
                    value={form.docType}
                    onChange={(v) => {
                      const svc = findServiceFor(catalogServices.services, v);
                      const allowed = (svc?.typePricing || []).map((e) => e.type);
                      set({
                        docType: v,
                        ...(allowed.length && !allowed.includes(form.translationType)
                          ? { translationType: allowed[0] }
                          : {}),
                      });
                      setFiles((prev) => prev.map((f) => ({ ...f, docType: v })));
                    }}
                    groups={docSelectGroups}
                    otherOption={{ value: 'other', label: 'Autre' }}
                    placeholder="— Sélectionner —"
                    ariaLabel="Type de document"
                  />
                  {form.docType === 'other' && (
                    <input
                      style={{ marginTop: 8 }}
                      value={form.docTypeOther}
                      onChange={(e) => set({ docTypeOther: e.target.value })}
                      placeholder="Précisez le type de document"
                    />
                  )}
                  {(errors.docType || errors.docTypeOther) && (
                    <small className="qf-hint qf-error-text">{errors.docType || errors.docTypeOther}</small>
                  )}
                </Field>
              )}

              <p className="qf-section">{form.prestation === 'translation' ? 'Langues de traduction' : 'Langues de la mission'}</p>
              <div className="qf-grid">
                <Field label="Langue source" required>
                  <LangFlagSelect
                    value={form.sourceLang}
                    onChange={(v) => set({ sourceLang: v })}
                    options={langs.map((l) => l.code)}
                    meta={langMeta}
                    placeholder="— Sélectionner —"
                    ariaLabel="Langue source"
                  />
                  {errors.sourceLang && <small className="qf-hint qf-error-text">{errors.sourceLang}</small>}
                </Field>
                <Field label="Langue cible" required>
                  <LangFlagSelect
                    value={form.targetLang}
                    onChange={(v) => set({ targetLang: v })}
                    options={langs.map((l) => l.code)}
                    meta={langMeta}
                    placeholder="— Sélectionner —"
                    ariaLabel="Langue cible"
                  />
                  {errors.targetLang && <small className="qf-hint qf-error-text">{errors.targetLang}</small>}
                </Field>
              </div>
              {form.prestation !== 'translation' && (
                <>
                  <div className="qf-grid">
                    <Field label={form.multiDay ? 'Date de début' : 'Date souhaitée'} required>
                      <input type="date" value={form.missionDate} onChange={(e) => set({ missionDate: e.target.value })} />
                      {errors.missionDate && <small className="qf-hint qf-error-text">{errors.missionDate}</small>}
                    </Field>
                    {form.multiDay && (
                      <Field label="Date de fin" required>
                        <input type="date" min={form.missionDate || undefined} value={form.missionEndDate} onChange={(e) => set({ missionEndDate: e.target.value })} />
                        {errors.missionEndDate && <small className="qf-hint qf-error-text">{errors.missionEndDate}</small>}
                      </Field>
                    )}
                    <Field label="Heure de début">
                      <input type="time" value={form.missionStartTime} onChange={(e) => set({ missionStartTime: e.target.value })} />
                    </Field>
                    <Field label={form.multiDay ? 'Durée par jour (heures)' : 'Durée estimée (heures)'} required>
                      <input type="number" min="1" max="24" value={form.duration} onChange={(e) => set({ duration: e.target.value })} placeholder="2" />
                      {errors.duration && <small className="qf-hint qf-error-text">{errors.duration}</small>}
                    </Field>
                    <Field label="Nombre de participants">
                      <input type="number" min="1" value={form.participants} onChange={(e) => set({ participants: e.target.value })} placeholder="Facultatif" />
                    </Field>
                  </div>
                  <Field label="La mission se déroule-t-elle sur plusieurs jours ?">
                    <div className="qf-checks">
                      <label className="qf-check">
                        <input type="radio" name="multiDay" checked={!form.multiDay} onChange={() => set({ multiDay: false, missionEndDate: '' })} />
                        <span>Non</span>
                      </label>
                      <label className="qf-check">
                        <input type="radio" name="multiDay" checked={form.multiDay} onChange={() => set({ multiDay: true })} />
                        <span>Oui</span>
                      </label>
                    </div>
                    {form.multiDay && (
                      <small className="qf-hint" style={{ display: 'block', marginTop: 6 }}>
                        La disponibilité de l’interprète et le prix seront vérifiés sur l’ensemble des journées avant confirmation.
                      </small>
                    )}
                  </Field>
                  {isOnsite && (
                    <div className="qf-grid">
                      <Field label="Ville de la mission" required>
                        <SearchableSelect
                          value={form.missionCity}
                          onChange={(v) => set({ missionCity: v })}
                          groups={[{ label: 'Tunisie', items: getVilleOptions('TN').map((v) => ({ value: v.name, label: v.name })) }]}
                          placeholder="Sélectionnez une ville…"
                          ariaLabel="Ville de la mission"
                        />
                        {errors.missionCity && <small className="qf-hint qf-error-text">{errors.missionCity}</small>}
                      </Field>
                      <Field label="Adresse" required>
                        <input value={form.missionAddress} onChange={(e) => set({ missionAddress: e.target.value })} placeholder="Lieu exact de la mission" />
                        {errors.missionAddress && <small className="qf-hint qf-error-text">{errors.missionAddress}</small>}
                      </Field>
                    </div>
                  )}
                  {isRemote && (
                    <Field label="Lien de visioconférence" full>
                      <input value={form.meetingLink} onChange={(e) => set({ meetingLink: e.target.value })} placeholder="https://… (facultatif)" />
                    </Field>
                  )}
                </>
              )}
            </div>
          )}

          {step === 3 && form.prestation === 'translation' && (
            <div className="qf-stack">
              <Field label="1. Document à traduire" required>
                <div
                  className="qf-upload"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    aria-label="Déposer votre document"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                  />
                  <UploadCloud size={26} />
                  <span>Déposez votre document ici ou cliquez pour parcourir</span>
                  <small>PDF, JPG, PNG, DOCX — pages et mots détectés automatiquement (max 500 Mo)</small>
                </div>
                {files.length > 0 && (
                  <ul className="qf-files">
                    {files.map((f, i) => (
                      <li key={`${f.file.name}-${i}`}>
                        {f.preview && <img className="of-file-thumb" src={f.preview} alt="" />}
                        <span className="of-file-name">
                          <FileText size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
                          {f.file.name} <small className="of-file-size">{formatSize(f.file.size)}</small>
                          {f.analyzing && <small className="of-file-words">Analyse en cours…</small>}
                          {!f.analyzing && f.analysis && (
                            <small className="of-file-words">
                              {[
                                f.analysis.pages ? `${f.analysis.pages} page${f.analysis.pages > 1 ? 's' : ''}` : '',
                                f.analysis.words > 0 ? `environ ${f.analysis.words.toLocaleString('fr-FR')} mots` : 'comptage des mots à vérifier',
                              ].filter(Boolean).join(' · ')}
                            </small>
                          )}
                        </span>
                        {files.length > 1 && (
                          <span className="of-file-type">
                            <SearchableSelect
                              compact
                              value={f.docType}
                              onChange={(v) => setFileType(i, { docType: v })}
                              groups={docSelectGroups}
                              otherOption={{ value: 'other', label: 'Autre' }}
                              placeholder="Type de document…"
                              ariaLabel={`Type de document pour ${f.file.name}`}
                            />
                            {f.docType === 'other' && (
                              <input
                                value={f.docTypeOther}
                                onChange={(e) => setFileType(i, { docTypeOther: e.target.value })}
                                placeholder="Précisez"
                              />
                            )}
                          </span>
                        )}
                        <button type="button" onClick={() => removeFile(i)} aria-label="Retirer le fichier">
                          <X size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {errors.files && <small className="qf-hint qf-error-text">{errors.files}</small>}
                <small className="qf-hint">
                  <ShieldCheck size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                  Le nombre de pages et de mots est détecté automatiquement lorsque le format le permet. La méthode de tarification est déterminée par le catalogue.
                </small>
              </Field>

              <Field label="2. Formalité complémentaire" required>
                <small className="qf-hint" style={{ marginBottom: 8, display: 'block' }}>
                  Souhaitez-vous une formalité supplémentaire pour votre document ?
                </small>
                <div className="qf-checks qf-checks-col">
                  {FORMALITIES.map((f) => (
                    <label key={f.id} className="qf-check">
                      <input
                        type="radio"
                        name="formality"
                        checked={form.formality === f.id}
                        onChange={() => set({
                          formality: f.id,
                          ...(f.id === 'none'
                            ? { useCountry: '', recipientOrg: '' }
                            : {}),
                        })}
                      />
                      <span>{f.label}</span>
                    </label>
                  ))}
                </div>
                {errors.formality && <small className="qf-hint qf-error-text">{errors.formality}</small>}
              </Field>

              {needsDestination && (
                <>
                  <div className="qf-grid">
                    <Field label="Pays où le document sera utilisé" required>
                      <SearchableSelect
                        value={form.useCountry}
                        onChange={(v) => set({ useCountry: v })}
                        groups={[COUNTRY_GROUP]}
                        otherOption={{ value: 'unknown', label: 'Je ne sais pas' }}
                        placeholder="— Sélectionner un pays —"
                        ariaLabel="Pays où le document sera utilisé"
                      />
                      {errors.useCountry && <small className="qf-hint qf-error-text">{errors.useCountry}</small>}
                    </Field>
                    <Field label="Organisme destinataire">
                      <input
                        value={form.recipientOrg}
                        onChange={(e) => set({ recipientOrg: e.target.value })}
                        placeholder="Ambassade, université, tribunal… (facultatif)"
                      />
                    </Field>
                  </div>
                </>
              )}

              <small className="qf-hint">
                <ShieldCheck size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                La faisabilité et le prix des formalités seront vérifiés selon le document et le pays de destination. Si une vérification est nécessaire, l'équipe vous précisera les démarches avant confirmation.
                {' '}Le tarif affiché couvre la formalité elle-même — l'intervention d'un avocat, d'un notaire ou d'une administration, si nécessaire, est confirmée et facturée séparément.
              </small>
            </div>
          )}

          {step === 3 && form.prestation === 'interpreting' && (
            <div className="qf-stack">
              <Field label="Sujet et contexte de la mission" full>
                <small className="qf-hint" style={{ display: 'block', marginBottom: 6 }}>
                  Décrivez le thème, les interlocuteurs et le cadre — cela aide à trouver l’interprète adapté.
                </small>
                <textarea rows={4} value={form.subject} onChange={(e) => set({ subject: e.target.value })} />
              </Field>
              <Field label="Documents préparatoires (facultatif)" full>
                <div
                  className="qf-upload"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); addPrepFiles(e.dataTransfer.files); }}
                >
                  <input
                    ref={prepInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    aria-label="Documents préparatoires"
                    onChange={(e) => { addPrepFiles(e.target.files); e.target.value = ''; }}
                  />
                  <UploadCloud size={26} />
                  <span>Ordre du jour, présentation, glossaire…</span>
                  <small>PDF, JPG, PNG, DOCX — aide l’interprète à préparer la mission (max 500 Mo, {MAX_PREP_FILES} fichiers max)</small>
                </div>
                {interpFiles.length > 0 && (
                  <ul className="qf-files">
                    {interpFiles.map((f, i) => (
                      <li key={`${f.file.name}-${i}`}>
                        {f.preview && <img className="of-file-thumb" src={f.preview} alt="" />}
                        <span className="of-file-name">
                          <FileText size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
                          {f.file.name} <small className="of-file-size">{formatSize(f.file.size)}</small>
                          {f.analyzing && <small className="of-file-words">Analyse en cours…</small>}
                          {!f.analyzing && f.analysis && (
                            <small className="of-file-words">
                              {[
                                f.analysis.pages ? `${f.analysis.pages} page${f.analysis.pages > 1 ? 's' : ''}` : '',
                                f.analysis.words > 0 ? `environ ${f.analysis.words.toLocaleString('fr-FR')} mots` : '',
                              ].filter(Boolean).join(' · ')}
                            </small>
                          )}
                        </span>
                        <button type="button" onClick={() => removePrepFile(i)} aria-label="Retirer le fichier">
                          <X size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Field>
            </div>
          )}

          {step === 4 && (
            <div className="qf-stack">
              <Field label="1. Délai souhaité" required>
                <div className="of-presta of-presta--3">
                  {DEADLINES.map((dd) => {
                    const extra = DEADLINE_EXTRA[dd.id] || 0;
                    return (
                      <label key={dd.id} className={`of-presta-card of-dlv-card${form.deadline === dd.id ? ' active' : ''}`}>
                        <input
                          type="radio"
                          name="deadline"
                          checked={form.deadline === dd.id}
                          onChange={() => set({ deadline: dd.id })}
                        />
                        <strong>{dd.label}</strong>
                        <small>{dd.desc}</small>
                        {extra > 0 && <em className="of-dlv-fee">+{formatTndWithEur(extra, 'fr-FR', 'TND')}</em>}
                      </label>
                    );
                  })}
                </div>
              </Field>

              {form.prestation === 'translation' && (
                <>
                  <p className="qf-section">2. Date souhaitée de réception</p>
                  <div className="qf-grid">
                    <Field label="Date souhaitée" required>
                      <input type="date" value={form.deliveryDate} onChange={(e) => set({ deliveryDate: e.target.value })} />
                      {errors.deliveryDate && <small className="qf-hint qf-error-text">{errors.deliveryDate}</small>}
                    </Field>
                    <Field label="Heure souhaitée">
                      <select value={form.deliveryTime} onChange={(e) => set({ deliveryTime: e.target.value })}>
                        <option value="">Indifférent</option>
                        {DELIVERY_TIMES.map((h) => <option key={h} value={h}>Avant {h}</option>)}
                      </select>
                      <small className="qf-hint">Échéance de réception souhaitée — pas une heure de début de traitement.</small>
                    </Field>
                  </div>
                  <Field label="Flexibilité du délai" required>
                    <div className="qf-checks qf-checks-col">
                      {DEADLINE_FLEX.map((f) => (
                        <label key={f.id} className="qf-check">
                          <input
                            type="radio"
                            name="deadlineFlex"
                            checked={form.deadlineFlex === f.id}
                            onChange={() => set({ deadlineFlex: f.id })}
                          />
                          <span>{f.label}</span>
                        </label>
                      ))}
                    </div>
                    {errors.deadlineFlex && <small className="qf-hint qf-error-text">{errors.deadlineFlex}</small>}
                    {form.deadlineFlex === 'strict' && (
                      <small className="qf-hint">
                        La faisabilité de cette échéance sera vérifiée avant confirmation — si le délai n’est pas réalisable, une autre date vous sera proposée.
                      </small>
                    )}
                  </Field>
                </>
              )}

              {form.prestation === 'translation' && (
                <>
                  <Field label="3. Mode de livraison — comment souhaitez-vous recevoir votre traduction ?" required>
                    <div className="of-presta of-presta--delivery">
                      {deliveryOpts.options.map((o) => {
                        const Icon = DELIVERY_ICONS[o.icon] || Package;
                        const fee = resolveDeliveryFee(o, form.deliveryCountry);
                        const feeLabel = o.requiresAddress && fee == null
                          ? 'Frais selon la destination'
                          : fee > 0
                            ? `+${formatTndWithEur(fee, 'fr-FR', 'TND')}`
                            : 'Sans frais de livraison';
                        return (
                          <label key={o.code} className={`of-presta-card of-dlv-card${form.deliveryMode === o.code ? ' active' : ''}`}>
                            <input
                              type="radio"
                              name="delivery"
                              checked={form.deliveryMode === o.code}
                              onChange={() => set({ deliveryMode: o.code })}
                            />
                            <span className="of-dlv-head">
                              <Icon size={18} />
                              <strong>{o.labelFr}</strong>
                            </span>
                            {o.description && <small>{o.description}</small>}
                            <em className="of-dlv-fee">{feeLabel}</em>
                          </label>
                        );
                      })}
                    </div>
                    {deliveryOpts.offline && (
                      <small className="qf-hint qf-error-text">Options de livraison indisponibles — vérifiez la connexion au serveur.</small>
                    )}
                  </Field>

                  {selectedDelivery?.requiresAddress && (
                    <div className="qf-stack">
                      <p className="qf-section">Adresse de livraison</p>
                      <div className="qf-grid qf-grid--3">
                        <Field label="Nom du destinataire" required>
                          <input value={form.recipient} onChange={(e) => set({ recipient: e.target.value })} placeholder={`${form.firstName} ${form.lastName}`.trim() || 'Nom et prénom'} autoComplete="name" />
                          {errors.recipient && <small className="qf-hint qf-error-text">{errors.recipient}</small>}
                        </Field>
                        <Field label="Pays de livraison" required>
                          <SearchableSelect
                            groups={[COUNTRY_GROUP]}
                            value={form.deliveryCountry}
                            onChange={(v) => set({ deliveryCountry: v, deliveryCity: '' })}
                            placeholder="Sélectionnez un pays…"
                          />
                          {errors.deliveryCountry && <small className="qf-hint qf-error-text">{errors.deliveryCountry}</small>}
                        </Field>
                        <Field label="Ville" required>
                          {deliveryVilleOptions.length ? (
                            <SearchableSelect
                              value={form.deliveryCity}
                              onChange={(v) => set({ deliveryCity: v })}
                              groups={[{ label: '', items: deliveryVilleOptions.map((v) => ({ value: v.name, label: v.name })) }]}
                              placeholder="Sélectionnez une ville…"
                              ariaLabel="Ville de livraison"
                            />
                          ) : (
                            <input value={form.deliveryCity} onChange={(e) => set({ deliveryCity: e.target.value })} autoComplete="address-level2" />
                          )}
                          {errors.deliveryCity && <small className="qf-hint qf-error-text">{errors.deliveryCity}</small>}
                        </Field>
                      </div>
                      <div className="qf-grid">
                        <Field label="Adresse complète" required>
                          <input value={form.address} onChange={(e) => set({ address: e.target.value })} placeholder="Rue, numéro, immeuble…" autoComplete="street-address" />
                          {errors.address && <small className="qf-hint qf-error-text">{errors.address}</small>}
                        </Field>
                        <Field label="Code postal">
                          <input value={form.postalCode} onChange={(e) => set({ postalCode: e.target.value })} autoComplete="postal-code" />
                        </Field>
                      </div>
                      <small className="qf-hint">Le téléphone indiqué à l'étape « Coordonnées » sera utilisé pour la livraison.</small>
                    </div>
                  )}
                </>
              )}

              <Field label="4. Informations complémentaires (facultatif)" full>
                <small className="qf-hint" style={{ display: 'block', marginBottom: 6 }}>Informations utiles pour le traducteur ou l'équipe.</small>
                <textarea rows={3} value={form.instructions} onChange={(e) => set({ instructions: e.target.value })} />
              </Field>
            </div>
          )}

          {step === 5 && (
            <div className="qf-stack">
              <p className="qf-section">Vos coordonnées</p>
              <div className="qf-grid qf-grid--3">
                <Field label="Civilité" required>
                  <select value={form.civility} onChange={(e) => set({ civility: e.target.value })} autoComplete="honorific-prefix">
                    <option value="">Sélectionner</option>
                    {civilities.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {errors.civility && <small className="qf-hint qf-error-text">{errors.civility}</small>}
                </Field>
                <Field label="Prénom" required>
                  <input value={form.firstName} onChange={(e) => set({ firstName: e.target.value })} autoComplete="given-name" />
                  {errors.firstName && <small className="qf-hint qf-error-text">{errors.firstName}</small>}
                </Field>
                <Field label="Nom" required>
                  <input value={form.lastName} onChange={(e) => set({ lastName: e.target.value })} autoComplete="family-name" />
                  {errors.lastName && <small className="qf-hint qf-error-text">{errors.lastName}</small>}
                </Field>
              </div>
              <div className="qf-grid">
                <Field label="Adresse email" required>
                  <input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} autoComplete="email" placeholder="exemple@email.com" />
                  {errors.email && <small className="qf-hint qf-error-text">{errors.email}</small>}
                </Field>
                <Field label="Numéro de téléphone" required>
                  <input value={form.phone} onChange={(e) => set({ phone: e.target.value })} autoComplete="tel" placeholder="+216 …" />
                  {errors.phone && <small className="qf-hint qf-error-text">{errors.phone}</small>}
                </Field>
              </div>

              <p className="qf-section">Récapitulatif de la commande</p>
              <dl className="qf-confirm">
                <div><dt>Prestation</dt><dd>{form.prestation === 'translation' ? 'Traduction de documents' : 'Interprétariat'}</dd></div>
                <div><dt>Langues</dt><dd>{form.sourceLang || '—'} → {form.targetLang || '—'}</dd></div>
                {form.prestation === 'translation' ? (
                  <>
                    <div><dt>Type de traduction</dt><dd>{TRANSLATION_TYPES.find((tp) => tp.id === form.translationType)?.label || '—'}</dd></div>
                    <div><dt>Formalité</dt><dd>{FORMALITIES.find((f) => f.id === form.formality)?.label || 'Aucune'}</dd></div>
                    {needsDestination && (
                      <div><dt>Pays d'utilisation</dt><dd>{form.useCountry === 'unknown' ? 'Je ne sais pas' : form.useCountry || '—'}</dd></div>
                    )}
                    {needsDestination && form.recipientOrg.trim() && <div><dt>Organisme destinataire</dt><dd>{form.recipientOrg}</dd></div>}
                    <div><dt>Type de document</dt><dd>{form.docType === 'other' ? form.docTypeOther : svcName(docGroups.flatMap((g) => g.items).find((s) => s.slug === form.docType) || {}) || '—'}</dd></div>
                    <div><dt>Fichiers</dt><dd>{files.length ? files.map((f) => f.file.name).join(', ') : '—'}</dd></div>
                    <div><dt>Volume détecté</dt><dd>
                      {files.some((f) => f.analysis && (f.analysis.pages || f.analysis.words))
                        ? [
                            `${files.reduce((s, f) => s + (f.analysis?.pages || 0), 0) || files.reduce((s, f) => s + (f.analysis?.billablePages || 0), 0)} page(s)`,
                            `${files.reduce((s, f) => s + (f.analysis?.words || 0), 0).toLocaleString('fr-FR')} mots`,
                          ].filter(Boolean).join(' · ')
                        : files.some((f) => f.analyzing)
                          ? 'Analyse en cours…'
                          : 'Vérifié à la réception des fichiers'}
                    </dd></div>
                    <div><dt>Échéance</dt><dd>{form.deliveryDate ? `${form.deliveryDate}${form.deliveryTime ? ` · Avant ${form.deliveryTime}` : ''}` : '—'}{form.deadlineFlex === 'flexible' ? ' · flexible' : ''}</dd></div>
                  </>
                ) : (
                  <>
                    <div><dt>Type de mission</dt><dd>{interpTypeLabel(form.interpretingType) || '—'}</dd></div>
                    <div><dt>Mode</dt><dd>{typeName(interpretingModes.find((tp) => tp.slug === form.interpretingMode) || {}) || '—'}</dd></div>
                    <div><dt>Mission</dt><dd>
                      {form.missionDate || '—'}
                      {form.multiDay && form.missionEndDate ? ` → ${form.missionEndDate}` : ''}
                      {form.missionStartTime ? ` à ${form.missionStartTime}` : ''}
                      {form.duration ? ` · ${form.duration} h${form.multiDay ? '/jour' : ''}` : ''}
                    </dd></div>
                    {isOnsite && (form.missionCity || form.missionAddress) && (
                      <div><dt>Lieu</dt><dd>{[form.missionAddress, form.missionCity].filter(Boolean).join(', ')}</dd></div>
                    )}
                    {form.meetingLink && <div><dt>Visioconférence</dt><dd>{form.meetingLink}</dd></div>}
                    {form.participants && <div><dt>Participants</dt><dd>{form.participants}</dd></div>}
                    {form.subject && <div><dt>Sujet</dt><dd>{form.subject}</dd></div>}
                    {files.length > 0 && <div><dt>Documents préparatoires</dt><dd>{files.map((f) => f.file.name).join(', ')}</dd></div>}
                  </>
                )}
                <div><dt>Délai</dt><dd>{DEADLINES.find((dd) => dd.id === form.deadline)?.label}</dd></div>
                {form.prestation === 'translation' && (
                  <div><dt>Livraison</dt><dd>{selectedDelivery?.labelFr || form.deliveryMode}</dd></div>
                )}
                <div><dt>Coordonnées</dt><dd>
                  {[
                    [form.civility, form.firstName, form.lastName].filter(Boolean).join(' '),
                    form.email,
                    form.phone,
                  ].filter(Boolean).join(' · ') || '—'}
                </dd></div>
                {selectedDelivery?.requiresAddress && (
                  <div><dt>Adresse de livraison</dt><dd>
                    {[
                      form.recipient.trim() || [form.civility, form.firstName, form.lastName].filter(Boolean).join(' '),
                      form.address,
                      [form.postalCode, form.deliveryCity].filter(Boolean).join(' '),
                      form.deliveryCountry,
                    ].filter(Boolean).join(', ') || '—'}
                  </dd></div>
                )}
              </dl>

              {price.total > 0 && !price.pending ? (
                <>
                  <table className="qf-price-table">
                    <tbody>
                      {price.breakdown.map((item, i) => (
                        <tr key={i}><td>{item.label}</td><td>{formatTndWithEur(item.value)}</td></tr>
                      ))}
                      <tr className="qf-price-table-total"><td>Total estimé</td><td>{formatTndWithEur(price.total)}</td></tr>
                    </tbody>
                  </table>
                  {price.toConfirm && (
                    <small className="qf-hint">
                      Montant indicatif — les frais de formalité et/ou de livraison seront confirmés par l’équipe avant le paiement.
                    </small>
                  )}
                </>
              ) : (
                <p className="qf-hint">
                  {price.pending
                    ? 'Aucun tarif applicable dans le catalogue pour cette sélection — notre équipe confirmera le prix avant le paiement.'
                    : 'Complétez les étapes précédentes pour afficher l’estimation.'}
                </p>
              )}

              <div className="qf-checks qf-checks-col">
                <label className="qf-check">
                  <input
                    type="checkbox"
                    checked={form.termsAccepted}
                    onChange={(e) => set({ termsAccepted: e.target.checked })}
                  />
                  <span>J&apos;accepte les conditions de service de Tarjemlek.</span>
                </label>
              </div>
              {errors.terms && <small className="qf-hint qf-error-text">{errors.terms}</small>}

              <small className="qf-hint">
                <ShieldCheck size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                Le paiement est demandé uniquement après confirmation d’un professionnel.
              </small>
            </div>
          )}
        </div>

        <div className="qf-actions">
          {step > 1 ? (
            <button type="button" className="btn btn-outline" onClick={goPrev}>
              <ArrowLeft size={16} /> Précédent
            </button>
          ) : (
            <span />
          )}
          {step < steps.length ? (
            <button type="button" className="btn btn-primary" onClick={goNext}>
              Suivant <ArrowRight size={16} />
            </button>
          ) : (
            <button type="button" className="btn btn-primary" disabled={submitting || catalogOffline} onClick={handleSubmit}>
              {submitting ? 'Envoi en cours…' : 'Envoyer ma demande'}
            </button>
          )}
        </div>
      </form>

      <DashModal
        open={confirmOpen}
        title="Confirmer votre commande"
        subtitle="Vérifiez vos coordonnées avant l'envoi"
        onClose={() => setConfirmOpen(false)}
      >
        <div style={{ display: 'grid', gap: 12 }}>
          <dl className="ord-kv">
            <dt>Coordonnées</dt>
            <dd>{[form.civility, form.firstName, form.lastName].filter(Boolean).join(' ')} · {form.email} · {form.phone}</dd>
            <dt>Prestation</dt>
            <dd>{form.prestation === 'translation' ? 'Traduction de documents' : 'Interprétariat'}</dd>
            <dt>Langues</dt>
            <dd>{form.sourceLang} → {form.targetLang}</dd>
            {!price.pending && price.total > 0 && (
              <>
                <dt>Prix estimé</dt>
                <dd>{formatTndWithEur(price.total)}</dd>
              </>
            )}
          </dl>
          <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--gray-600)' }}>
            En confirmant, votre demande et vos coordonnées seront envoyées à Tarjemlek.
            Vous pourrez suivre votre commande et serez informé dès qu'un professionnel l'acceptera.
          </p>
          <div className="dash-actions" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-outline" onClick={() => setConfirmOpen(false)}>
              Modifier
            </button>
            <button type="button" className="btn btn-primary" disabled={submitting} onClick={doSubmit}>
              {submitting ? 'Envoi en cours…' : 'Confirmer et envoyer'}
            </button>
          </div>
        </div>
      </DashModal>
    </div>
  );
}
